import { useState } from 'react';
import { useConfirmLeave, useUnsavedWork } from '../../contexts/UnsavedWorkContext';
import { useParams, useSearchParams } from 'react-router-dom';
import { ExternalLinkIcon, MergeIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { TabPanel, Tabs } from '../ui/Tabs';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { CustomerOverview } from './CustomerOverview';
import { PlanEditor } from './PlanEditor';
import { AppointmentsPanel } from './AppointmentsPanel';
import { CustomerSubscription } from './CustomerSubscription';
import { CustomerOrders } from './CustomerOrders';
import { CustomerDiary } from './CustomerDiary';
import { useCustomerFile, type CustomerFileControl } from './useCustomerFile';
import { attentionFor, customerName, shapeLabel, summarize, type CustomerTab } from './customerRules';
import { btnSecondary } from '../ui/styles';
import { shopHref } from '../../utils/hosts';
import { hasBackend } from '../../core/api/http';
import { useFeatures } from '../../hooks/useFeatures';
import { useAdmin } from '../AdminContext';
import { useCan } from '../useCan';
import { MergeCustomerDialog } from './MergeCustomerDialog';
const back = {
  to: '/be/klanten',
  label: 'Alle klanten'
};

/** Het dossier van één klant. Alles wat hier verandert, bewaart direct. */
export function CustomerDetail() {
  const {
    id
  } = useParams();
  const ctl = useCustomerFile(id);
  const features = useFeatures();
  const {
    site
  } = useAdmin();
  const [params, setParams] = useSearchParams();
  // Huidplan en notitie bewaren pas op de knop; zolang dat niet is gebeurd, niet stil weggooien.
  const [unsaved, setUnsaved] = useState(false);
  // Tabblad sluiten, doorklikken in het beheer én een ander tabblad hier: eerst dezelfde "Weggaan?".
  useUnsavedWork(unsaved, 'page');
  const confirmLeave = useConfirmLeave();
  const {
    canAll
  } = useCan();
  const [merging, setMerging] = useState(false);
  if (ctl.status === 'loading' && !ctl.file) {
    return <div className="flex items-center gap-2 py-16 text-[14px] text-muted" role="status" data-id="element-1158" data-mp="10bydpv1t0ouj">
        <Spinner data-id="element-1159" data-mp="vhkns85wv9x9" /> Dossier laden…
      </div>;
  }
  if (ctl.status === 'missing') return <MissingItem title="Dossier niet gevonden" text="Deze klant bestaat niet (meer), of de link is veranderd. Zoek haar op naam in de klantenlijst." back={back} data-id="element-1160" data-mp="rfqpu7n3ok0s" />;
  if (ctl.status === 'error' || !ctl.file) {
    return <LoadFailed title="Het dossier kon niet laden" onRetry={ctl.reload} data-id="element-1161" data-mp="1f37aqp81dchp" />;
  }

  // Hier is het dossier geladen; zo weten de tabbladen dat zonder omzetting.
  const control: CustomerFileControl = {
    ...ctl,
    file: ctl.file
  };
  const {
    account
  } = control.file;
  const summary = summarize(account);
  const flags = attentionFor(account, site.policy.revisitAfterWeeks);
  const allTabs: {
    key: CustomerTab;
    label: string;
    count?: number;
    hidden?: boolean;
  }[] = [{
    key: 'overzicht',
    label: 'Overzicht',
    count: flags.length || undefined
  }, {
    key: 'huidplan',
    label: 'Huidplan'
  }, {
    key: 'afspraken',
    label: 'Afspraken & kuur',
    count: account.appointments.filter(a => a.status === 'aanvraag').length || undefined
  }, {
    key: 'abonnement',
    label: 'Abonnement',
    hidden: !features.subscriptions
  }, {
    key: 'bestellingen',
    label: 'Bestellingen',
    count: account.orders.length || undefined
  }, {
    key: 'fotos',
    label: 'Fotodagboek',
    count: account.diary.length || undefined,
    hidden: !features.diary
  }];
  // Een uitgezet onderdeel (Instellingen › Koppelingen & privacy › Onderdelen) heeft geen tab; een oude link valt terug op Overzicht.
  const tabs = allTabs.filter(t => !t.hidden);
  const tab = tabs.find(t => t.key === params.get('tab'))?.key ?? 'overzicht';
  const goTo = (key: CustomerTab) => {
    if (key === tab) return;
    // 'page': het huidplan of de notitie van dit tabblad gaat verloren. Zonder werk: meteen.
    confirmLeave('page', () => {
      setUnsaved(false);
      setParams(key === 'overzicht' ? {} : {
        tab: key
      }, {
        replace: true
      });
    });
  };
  return <div data-id="element-1162" data-mp="2udlv1ttleogl">
      <PageHeader title={customerName(account.customer)} description={[control.file.bookingBlock ? 'boekt niet online' : '', shapeLabel(summary.shape), account.customer.address.city, account.customer.email || account.customer.phone].filter(Boolean).join(' · ')} back={back} actions={<>
            {canAll('customers.merge') && <button type="button" onClick={() => setMerging(true)} className={btnSecondary} data-id="element-1164" data-mp="2bgavs97ynv15">
                <MergeIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1165" data-mp="2pfkcda02wr5n" />
                Samenvoegen
              </button>}
            <a href={shopHref('/account')} target="_blank" rel="noreferrer" className={btnSecondary} data-id="element-1166" data-mp="4niiarl0593l">
              Mijn omgeving
              <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1167" data-mp="3vtjnijiad8hb" />
              <span className="sr-only" data-id="element-1168" data-mp="1hlsinuxz3vry"> (opent in een nieuw tabblad)</span>
            </a>
          </>} data-id="element-1163" data-mp="2udvltjmgoznc" />
      {canAll('customers.merge') && <MergeCustomerDialog open={merging} onClose={() => setMerging(false)} account={account} onMerged={() => ctl.reload()} data-id="element-1169" data-mp="18lvojdste4pi" />}

      <Tabs id="dossier" label="Onderdeel van het dossier" className="mb-6" tabs={tabs} value={tab} onChange={goTo} data-id="element-1170" data-mp="1dk6xznf76d77" />

      <TabPanel id="dossier" value={tab} data-id="element-1171" data-mp="21buhhka4qlzg">
        {tab === 'overzicht' && <CustomerOverview ctl={control} onTab={goTo} onDirtyChange={setUnsaved} data-id="element-1172" data-mp="25bwirsbydde7" />}
        {tab === 'huidplan' && <PlanEditor ctl={control} onDirtyChange={setUnsaved} data-id="element-1173" data-mp="1rl9n691ho3mc" />}
        {tab === 'afspraken' && <AppointmentsPanel ctl={control} data-id="element-1174" data-mp="1wjky6n3isk65" />}
        {tab === 'abonnement' && <CustomerSubscription ctl={control} data-id="element-1175" data-mp="1whq7st9hbzuo" />}
        {tab === 'bestellingen' && <CustomerOrders ctl={control} data-id="element-1176" data-mp="2k7ucz52bg7ns" />}
        {tab === 'fotos' && <CustomerDiary ctl={control} data-id="element-1177" data-mp="2p0nihv5lzk0x" />}
      </TabPanel>

      <p className="mt-8 text-[12px] text-muted" data-id="element-1178" data-mp="2p28cnjo0h0y9">
        Wijzigingen in een dossier worden per actie bewaard en staan direct in Mijn omgeving van {account.customer.firstName}.
        {!hasBackend && ' Demo: gedeeld met de voorbeeldaccounts op /account.'}
      </p>
    </div>;
}